import { FC, useState } from 'react'
import {
  CreateBase, DeleteWithConfirmButton, EditBase, SelectInput,
  SimpleForm, TextInput, required, useGetList, useNotify,
  useRecordContext, useRefresh, useTranslate,
} from 'react-admin'
import { useFormContext, useWatch } from 'react-hook-form'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import {
  Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogContent, DialogTitle,
  FormControlLabel, FormGroup, IconButton, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography,
} from '@mui/material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import {
  ApplicationEnvironmentOut, ApplicationHeaderOut, ApplicationOut,
} from '../../types/apiRequests'
import { VALUE_TYPE_CHOICES, USER_SOURCE_CHOICES } from './headerConstants'
import { getJson, getUrl } from '../../providers/data'
import { SaveOnlyToolbar } from '../../components'

const VALUE_TYPE_LABEL_KEYS: Record<string, string> = {
  custom: 'data.valueTypeCustom',
  organization: 'data.valueTypeOrganization',
  user: 'data.valueTypeUser',
}

const VALUE_TYPE_COLORS: Record<string, 'default' | 'primary' | 'secondary'> = {
  custom: 'default',
  organization: 'primary',
  user: 'secondary',
}


const ConditionalValueFields: FC = () => {
  const translate = useTranslate()
  const valueType = useWatch({ name: 'value_type' }) as string | undefined

  if (valueType === 'custom') {
    return <TextInput source="custom_value" label="data.headerCustomValue" fullWidth validate={required()} />
  }
  if (valueType === 'organization') {
    return (
      <TextInput
        source="value_source"
        label="data.headerSource"
        validate={required()}
        fullWidth
        helperText={translate('data.orgMetadataHelp')}
      />
    )
  }
  if (valueType === 'user') {
    return (
      <SelectInput
        source="value_source"
        label="data.headerSource"
        choices={USER_SOURCE_CHOICES}
        validate={required()}
        fullWidth
      />
    )
  }
  return null
}

const EnvironmentCheckboxes: FC<{ applicationId: string }> = ({ applicationId }) => {
  const translate = useTranslate()
  const { setValue, watch } = useFormContext()
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const selected: string[] = watch('environment_ids') || []

  const { data: environments } = useGetList<ApplicationEnvironmentOut>(
    'application_environment',
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'name', order: 'ASC' },
      // eslint-disable-next-line camelcase
      filter: { application_id: applicationId },
    },
    { enabled: !!applicationId },
  )

  if (!environments || environments.length === 0) return null

  const handleToggle = (envId: string) => {
    const next = selected.includes(envId)
      ? selected.filter((id) => id !== envId)
      : [...selected, envId]
    setValue('environment_ids', next, { shouldDirty: true })
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        {translate('data.headerEnvironments')}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {translate('data.headerEnvironmentsHelp')}
      </Typography>
      <FormGroup row>
        {environments.map((env) => (
          <FormControlLabel
            key={env.id}
            control={
              <Checkbox
                size="small"
                checked={selected.includes(env.id)}
                onChange={() => handleToggle(env.id)}
              />
            }
            label={env.display_name}
          />
        ))}
      </FormGroup>
    </Box>
  )
}

const HeaderForm: FC<{ applicationId: string; defaultValues?: Record<string, unknown> }> = ({
  applicationId,
  defaultValues,
}) => {
  const translate = useTranslate()
  const isTokenRequest = defaultValues?.target === 'token_request'
  return (
    <SimpleForm
      // eslint-disable-next-line camelcase
      defaultValues={{ application_id: applicationId, ...defaultValues }}
      toolbar={<SaveOnlyToolbar alwaysEnable />}
    >
      <TextInput
        source="header_name"
        label={isTokenRequest ? 'data.tokenParamName' : 'data.headerName'}
        fullWidth
        validate={required()}
      />
      <SelectInput
        source="value_type"
        label="data.headerType"
        choices={VALUE_TYPE_CHOICES}
        validate={required()}
        fullWidth
      />
      <ConditionalValueFields />
      <TextInput source="value_prefix" label="data.headerPrefix" fullWidth helperText={translate('data.headerPrefixHelp')} />
      <EnvironmentCheckboxes applicationId={applicationId} />
      <TextInput source="target" sx={{ display: 'none' }} />
    </SimpleForm>
  )
}

const CreateHeaderDialog: FC<{
  open: boolean;
  onClose: () => void;
  applicationId: string;
  target?: string;
}> = ({ open, onClose, applicationId, target = 'upstream_request' }) => {
  const refresh = useRefresh()
  const notify = useNotify()
  const translate = useTranslate()

  const onSuccess = () => {
    onClose()
    notify(translate(target === 'token_request' ? 'data.tokenParamCreated' : 'data.headerCreated'), { type: 'info' })
    refresh()
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{translate(target === 'token_request' ? 'data.addTokenParamTitle' : 'data.addHeaderTitle')}</DialogTitle>
      <DialogContent>
        <CreateBase resource="application_header" redirect={false} mutationOptions={{ onSuccess }}>
          <HeaderForm applicationId={applicationId} defaultValues={{ target }} />
        </CreateBase>
      </DialogContent>
    </Dialog>
  )
}

const EditHeaderDialog: FC<{
  open: boolean;
  onClose: () => void;
  header: ApplicationHeaderOut | null;
  applicationId: string;
  target?: string;
}> = ({ open, onClose, header, applicationId, target = 'upstream_request' }) => {
  const refresh = useRefresh()
  const notify = useNotify()
  const translate = useTranslate()

  if (!header) return null

  const onSuccess = () => {
    onClose()
    notify(translate(target === 'token_request' ? 'data.tokenParamUpdated' : 'data.headerUpdated'), { type: 'info' })
    refresh()
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{translate(target === 'token_request' ? 'data.editTokenParam' : 'data.editHeader')}</DialogTitle>
      <DialogContent>
        <EditBase resource="application_header" id={header.id} mutationMode="pessimistic" redirect={false} mutationOptions={{ onSuccess }}>
          <HeaderForm
            applicationId={applicationId}
            defaultValues={{
              // eslint-disable-next-line camelcase
              header_name: header.header_name,
              // eslint-disable-next-line camelcase
              value_type: header.value_type,
              // eslint-disable-next-line camelcase
              value_source: header.value_source,
              // eslint-disable-next-line camelcase
              custom_value: header.custom_value,
              // eslint-disable-next-line camelcase
              value_prefix: header.value_prefix,
              // eslint-disable-next-line camelcase
              environment_ids: header.environment_ids || [],
              target,
            }}
          />
        </EditBase>
      </DialogContent>
    </Dialog>
  )
}

const getValueDisplay = (header: ApplicationHeaderOut): string => {
  const prefix = header.value_prefix || ''
  if (header.value_type === 'custom') return `${prefix}${header.custom_value || '-'}`
  return `${prefix}{${header.value_source || '-'}}`
}

const RevealableValue: FC<{ header: ApplicationHeaderOut }> = ({ header }) => {
  const [revealedValue, setRevealedValue] = useState<string | null>(null)
  const isSecret = header.value_type === 'custom' && header.custom_value === '***'
  const displayValue = getValueDisplay(header)

  const handleToggle = async () => {
    if (revealedValue !== null) {
      setRevealedValue(null)
      return
    }
    if (!isSecret) return
    try {
      const data = await getJson<ApplicationHeaderOut>(
        `${getUrl()}/application_header/${header.id}/reveal`,
      )
      const prefix = header.value_prefix || ''
      setRevealedValue(`${prefix}${data.custom_value || '-'}`)
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status
      if (status === 401 || status === 403) {
        setRevealedValue('(not authorized)')
      } else {
        setRevealedValue('(decryption failed)')
      }
    }
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
        {revealedValue !== null ? revealedValue : (isSecret ? '••••••••' : displayValue)}
      </Typography>
      {isSecret && (
        <IconButton size="small" onClick={() => { void handleToggle() }} sx={{ p: 0.25 }}>
          {revealedValue !== null
            ? <VisibilityOffIcon sx={{ fontSize: 14, color: 'rgba(0,0,0,0.3)' }} />
            : <VisibilityIcon sx={{ fontSize: 14, color: 'rgba(0,0,0,0.3)' }} />
          }
        </IconButton>
      )}
    </Box>
  )
}

const EnvironmentChips: FC<{ header: ApplicationHeaderOut }> = ({ header }) => {
  const translate = useTranslate()
  const envNames = header.environment_names
  if (envNames && envNames.length > 0) {
    return (
      <>
        {envNames.map((name) => (
          <Chip key={name} label={name} size="small" variant="outlined" sx={{ mr: 0.5, mb: 0.25 }} />
        ))}
      </>
    )
  }
  return <Chip label={translate('data.allEnvironments')} size="small" color="default" />
}

const HeaderTable: FC<{
  headers: ApplicationHeaderOut[];
  nameLabel: string;
  deleteConfirmKey: string;
  onEdit: (header: ApplicationHeaderOut) => void;
  onRefresh: () => void;
}> = ({ headers, nameLabel, deleteConfirmKey, onEdit, onRefresh }) => {
  const translate = useTranslate()
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{translate(nameLabel)}</TableCell>
            <TableCell>{translate('data.headerType')}</TableCell>
            <TableCell>{translate('data.headerValueSource')}</TableCell>
            <TableCell>{translate('data.headerEnvironments')}</TableCell>
            <TableCell align="right" />
          </TableRow>
        </TableHead>
        <TableBody>
          {headers.map((header) => (
            <TableRow key={header.id}>
              <TableCell>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {header.header_name}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={
                    VALUE_TYPE_LABEL_KEYS[header.value_type]
                      ? translate(VALUE_TYPE_LABEL_KEYS[header.value_type])
                      : header.value_type
                  }
                  color={VALUE_TYPE_COLORS[header.value_type] || 'default'}
                  size="small"
                />
              </TableCell>
              <TableCell>
                <RevealableValue header={header} />
              </TableCell>
              <TableCell>
                <EnvironmentChips header={header} />
              </TableCell>
              <TableCell align="right">
                <IconButton size="small" onClick={() => onEdit(header)}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <DeleteWithConfirmButton
                  resource="application_header"
                  record={header}
                  confirmTitle={translate('delete.confirmTitle')}
                  confirmContent={translate(deleteConfirmKey)}
                  mutationOptions={{ onSuccess: () => onRefresh() }}
                  redirect={false}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

const ApplicationHeaderTab: FC = () => {
  const record = useRecordContext<ApplicationOut>()
  const translate = useTranslate()
  const refresh = useRefresh()
  const [createOpen, setCreateOpen] = useState(false)
  const [editHeader, setEditHeader] = useState<ApplicationHeaderOut | null>(null)
  const [createTokenOpen, setCreateTokenOpen] = useState(false)
  const [editTokenParam, setEditTokenParam] = useState<ApplicationHeaderOut | null>(null)

  const { data: upstreamHeaders, isLoading: loadingUpstream } = useGetList<ApplicationHeaderOut>(
    'application_header',
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'header_name', order: 'ASC' },
      // eslint-disable-next-line camelcase
      filter: { application_id: record?.id, target: 'upstream_request' },
    },
    { enabled: !!record?.id },
  )

  const { data: tokenParams, isLoading: loadingToken } = useGetList<ApplicationHeaderOut>(
    'application_header',
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'header_name', order: 'ASC' },
      // eslint-disable-next-line camelcase
      filter: { application_id: record?.id, target: 'token_request' },
    },
    { enabled: !!record?.id && record.auth_type === 'oauth_client_credentials' },
  )

  if (!record?.id) return null
  if (loadingUpstream || loadingToken) return <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {translate('data.customHeadersDescription')}
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
        >
          {translate('data.addHeader')}
        </Button>
      </Box>

      {upstreamHeaders && upstreamHeaders.length > 0 ? (
        <HeaderTable
          headers={upstreamHeaders}
          nameLabel="data.headerName"
          deleteConfirmKey="data.deleteHeaderConfirm"
          onEdit={setEditHeader}
          onRefresh={refresh}
        />
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          {translate('data.noHeaders')}
        </Typography>
      )}

      <CreateHeaderDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        applicationId={record.id}
        target="upstream_request"
      />

      <EditHeaderDialog
        open={!!editHeader}
        onClose={() => setEditHeader(null)}
        header={editHeader}
        applicationId={record.id}
        target="upstream_request"
      />

      {record.auth_type === 'oauth_client_credentials' && (
        <Box sx={{ mt: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {translate('data.tokenParamsDescription')}
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setCreateTokenOpen(true)}
            >
              {translate('data.addTokenParam')}
            </Button>
          </Box>

          {tokenParams && tokenParams.length > 0 ? (
            <HeaderTable
              headers={tokenParams}
              nameLabel="data.tokenParamName"
              deleteConfirmKey="data.deleteTokenParamConfirm"
              onEdit={setEditTokenParam}
              onRefresh={refresh}
            />
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              {translate('data.noTokenParams')}
            </Typography>
          )}

          <CreateHeaderDialog
            open={createTokenOpen}
            onClose={() => setCreateTokenOpen(false)}
            applicationId={record.id}
            target="token_request"
          />

          <EditHeaderDialog
            open={!!editTokenParam}
            onClose={() => setEditTokenParam(null)}
            header={editTokenParam}
            applicationId={record.id}
            target="token_request"
          />
        </Box>
      )}
    </Box>
  )
}

export default ApplicationHeaderTab
